import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * ONE Save for a form made of nine independent resources.
 *
 * The reference's editor is a single scroll under a single Save. Ours cannot be one atomic write — the show,
 * its lineup, members, run of show, setlist, VIP, travel, financials and expenses are separate versioned
 * resources, each with its own `If-Match`, which is what makes concurrent edits safe (the legacy app had ONE
 * JSON document and no concurrency control at all: every save rewrote the band's entire schedule, last write
 * wins). So the footer Save fans OUT instead: it creates the show if it does not exist yet, then flushes
 * every section that is dirty, in section order.
 *
 * <b>Why a callback registry and not a portal.</b> The footer ribbon hosts the section's own Save button by
 * portal today, and that works while exactly one section is mounted. Under a single scroll all ten are
 * mounted, so ten buttons land in one ribbon. What the footer needs from a section is not its button, it is
 * its *intent*: "are you dirty, and here is how to commit you". That is a callback, and callbacks are stored
 * in a ref — re-registering identity-unstable closures as STATE would loop on every keystroke.
 *
 * <b>Failure is partial and honest.</b> A fan-out is not a transaction: section 3 can fail after 1 and 2
 * landed. `saveAll` therefore returns every failure rather than throwing on the first, the footer names the
 * sections that did not save, and the modal stays open. Silently reporting success because *most* of it
 * worked is the "no phantom success" rule, and it is the failure mode most worth getting right here.
 */
export interface SectionSaver {
  /** Section key, for the error message ("Lineup and Travel didn't save"). */
  label: string;
  /** Section order, so a fan-out commits top-to-bottom as the user reads it. */
  order: number;
  /** Is there anything to write? A clean section is skipped entirely — no request, no version bump. */
  isDirty: () => boolean;
  /** Commit, against a show that is guaranteed to exist by the time this is called. */
  save: (showId: string) => Promise<void>;
}

interface Registry {
  registerSaver: (key: string, saver: SectionSaver) => void;
  unregisterSaver: (key: string) => void;
  /** Basics registers this: create the show and return its id (or return the existing id unchanged). */
  registerEnsureShow: (fn: (() => Promise<string>) | null) => void;
  /** True when anything at all would be written — drives the footer Save's enabled state. */
  anyDirty: () => boolean;
  saveAll: () => Promise<{ showId: string | null; failures: string[] }>;
  /**
   * Publish how many people this save would INVITE, so the footer can say so out loud instead of letting a
   * push leave under a word as quiet as "Save".
   *
   * The SETTER lives here and the VALUE does not, which is not tidiness — it is the loop this file's class
   * comment warns about, met head on. `useState`'s setter is identity-stable, so this whole context value
   * stays a ref and a section may depend on it. Putting the NUMBER here too makes the value a fresh object
   * on every count change; `usePendingInvites` depends on the registry, so it would re-run, its cleanup
   * would write 0, that write would mint another object, and the editor would render until the test timed
   * out — observed, not theorised. The number rides its own context instead (`usePendingInviteCount`),
   * which only the footer reads.
   */
  setPendingInvites: (n: number) => void;
  /**
   * Publish the editor's LIVE member roster — the person ids Members (section 03) currently shows, INCLUDING
   * staged-but-unsaved additions. Financials (section 09) reads it so its per-member pay rows appear the
   * instant crew is added, rather than only after a save + reopen. Same "setter on the ref, value on its own
   * context" shape as `setPendingInvites`, and for the same anti-loop reason.
   */
  setEditorRoster: (personIds: string[]) => void;
  /**
   * Publish the editor's LIVE travel legs — so section 09 Expenses can show a travel-derived row the instant a
   * leg's cost is typed in section 07, before any save. The value carries a setter for the reimbursement flag,
   * which writes back into section 07's draft (and so persists on the travel save) — the flag has ONE home,
   * the leg, and section 09 never writes it independently. Same ref-setter / value-on-context shape as the
   * roster, for the same anti-loop reason.
   */
  setEditorTravel: (travel: EditorTravel | null) => void;
}

/** One live travel leg as section 09 needs it — enough to label it, show its cost, and toggle its flag. */
export interface EditorTravelItem {
  localKey: string;
  travelItemId?: string | null;
  kind: string;
  airline?: string | null;
  flightNumber?: string | null;
  propertyName?: string | null;
  costCents: number | null;
  needsReimbursement: boolean;
}

/** The editor's live travel draft plus the one writer of a leg's reimbursement flag (section 07's own state). */
export interface EditorTravel {
  items: EditorTravelItem[];
  setNeedsReimbursement: (localKey: string, needsReimbursement: boolean) => void;
}

const SaveRegistryContext = createContext<Registry | null>(null);

/** The invite count, on its OWN context so the registry itself can stay a ref — see `setPendingInvites`. */
const PendingInvitesContext = createContext(0);

/** The editor's live member roster (person ids incl. staged additions), or null when nothing has published it
 *  yet — a standalone section (no provider) or before Members' first effect. See `setEditorRoster`. */
const EditorRosterContext = createContext<string[] | null>(null);

/** The editor's live travel draft, or null outside the editor / before Travel's first publish. See
 *  `setEditorTravel`. Section 09 falls back to the SAVED travel when this is null. */
const EditorTravelContext = createContext<EditorTravel | null>(null);

/** How many people the footer's Save would invite. 0 outside the editor. */
export function usePendingInviteCount(): number {
  return useContext(PendingInvitesContext);
}

export function EditorSaveProvider({ children }: { children: ReactNode }) {
  const savers = useRef(new Map<string, SectionSaver>());
  const ensureShow = useRef<(() => Promise<string>) | null>(null);

  const registerSaver = useCallback((key: string, saver: SectionSaver) => {
    savers.current.set(key, saver);
  }, []);
  const unregisterSaver = useCallback((key: string) => {
    savers.current.delete(key);
  }, []);
  const registerEnsureShow = useCallback((fn: (() => Promise<string>) | null) => {
    ensureShow.current = fn;
  }, []);
  const anyDirty = useCallback(() => [...savers.current.values()].some((s) => s.isDirty()), []);

  const saveAll = useCallback(async () => {
    // 1) The show has to exist before any sub-resource can point at it. On an edit this returns the id it
    //    already has; on a create it POSTs. A failure here aborts — there is nothing to attach anything to.
    let showId: string;
    try {
      if (!ensureShow.current) return { showId: null, failures: ["Basics"] };
      showId = await ensureShow.current();
    } catch {
      return { showId: null, failures: ["Basics"] };
    }

    // 2) Then every dirty section, in the order they appear on screen, SEQUENTIALLY: several of these bump
    //    the same show row's version, so firing them in parallel would race their own `If-Match` guards.
    const failures: string[] = [];
    for (const saver of [...savers.current.values()].sort((a, b) => a.order - b.order)) {
      if (!saver.isDirty()) continue;
      try {
        await saver.save(showId);
      } catch {
        failures.push(saver.label);
      }
    }
    return { showId, failures };
  }, []);

  const [pendingInvites, setPendingInvites] = useState(0);
  const [editorRoster, setEditorRoster] = useState<string[] | null>(null);
  const [editorTravel, setEditorTravel] = useState<EditorTravel | null>(null);

  // Every member of this object is identity-stable — the callbacks by `useCallback` with no deps, the
  // setters by `useState` — so the ref may hold it and nothing downstream re-runs on a render.
  const value = useRef<Registry>({
    registerSaver, unregisterSaver, registerEnsureShow, anyDirty, saveAll, setPendingInvites, setEditorRoster,
    setEditorTravel,
  });
  return (
    <SaveRegistryContext.Provider value={value.current}>
      <PendingInvitesContext.Provider value={pendingInvites}>
        <EditorRosterContext.Provider value={editorRoster}>
          <EditorTravelContext.Provider value={editorTravel}>{children}</EditorTravelContext.Provider>
        </EditorRosterContext.Provider>
      </PendingInvitesContext.Provider>
    </SaveRegistryContext.Provider>
  );
}

/** Read the registry. Null when the wizard is mounted standalone (its own unit tests), which is why every
 *  hook below no-ops rather than throwing — a section must still work outside the modal. */
export function useSaveRegistry(): Registry | null {
  return useContext(SaveRegistryContext);
}

/**
 * Register a section's commit with the footer. The callbacks are read through a ref, so a section may pass
 * fresh closures every render without re-registering or looping.
 */
export function useSectionSave(key: string, saver: SectionSaver): void {
  const registry = useSaveRegistry();
  const latest = useRef(saver);
  latest.current = saver;

  useEffect(() => {
    if (!registry) return;
    registry.registerSaver(key, {
      get label() { return latest.current.label; },
      get order() { return latest.current.order; },
      isDirty: () => latest.current.isDirty(),
      save: (showId) => latest.current.save(showId),
    });
    return () => registry.unregisterSaver(key);
  }, [registry, key]);
}

/**
 * Tell the footer how many people this section's save would invite, so the button can say so. No-ops
 * outside the editor (a standalone section owns its own commit and its own label).
 */
export function usePendingInvites(count: number): void {
  const registry = useSaveRegistry();
  useEffect(() => {
    registry?.setPendingInvites(count);
    return () => registry?.setPendingInvites(0);
  }, [registry, count]);
}

/**
 * Publish the editor's live member roster (person ids, incl. staged additions) so another section can react
 * to it — Financials draws a pay row per member here. No-ops outside the editor.
 *
 * The effect keys on the CONTENT (`join`), never the array identity: `personIds` is a fresh array every
 * render, so an identity dep would re-run the effect → setState → re-render → new array → loop. The content
 * key settles the moment the roster stops changing. (`latest` carries the array into the effect without
 * putting it in the deps.)
 */
export function useEditorRoster(personIds: string[]): void {
  const registry = useSaveRegistry();
  const latest = useRef(personIds);
  latest.current = personIds;
  const key = personIds.join(",");
  useEffect(() => {
    registry?.setEditorRoster(latest.current);
    return () => registry?.setEditorRoster([]);
  }, [registry, key]);
}

/** The editor's live member roster, or null when nothing has published it (standalone, or pre-first-effect).
 *  Financials falls back to the server member list when this is null. */
export function useEditorRosterValue(): string[] | null {
  return useContext(EditorRosterContext);
}

/**
 * Publish the editor's live travel legs (section 07 Travel), so section 09 Expenses can preview a travel row as
 * soon as its cost is typed. No-ops outside the editor.
 *
 * The effect keys on the CONTENT that section 09 renders (localKey · cost · reimburse · label bits), never the
 * `travel` object identity — which is fresh every render and would loop. The `setNeedsReimbursement` callback
 * is carried through `latest` and uses a functional setState upstream, so republishing it only on content
 * changes never staleness-traps it. Passing `null` (e.g. a seat without money:travel) publishes nothing.
 */
export function useEditorTravel(travel: EditorTravel | null): void {
  const registry = useSaveRegistry();
  const latest = useRef(travel);
  latest.current = travel;
  const key = travel
    ? travel.items
        .map((i) => `${i.localKey}:${i.costCents}:${i.needsReimbursement}:${i.kind}:${i.airline ?? ""}:${i.flightNumber ?? ""}:${i.propertyName ?? ""}`)
        .join("|")
    : "";
  useEffect(() => {
    registry?.setEditorTravel(latest.current);
    return () => registry?.setEditorTravel(null);
  }, [registry, key]);
}

/** The editor's live travel draft, or null outside the editor. Section 09 falls back to the SAVED travel. */
export function useEditorTravelValue(): EditorTravel | null {
  return useContext(EditorTravelContext);
}

/**
 * A section's OWN Save button — rendered only when there is no registry.
 *
 * In the editor the ribbon's single Save commits everything dirty, so a per-section button is redundant
 * chrome: ten of them down one scroll, each doing a fraction of what the footer already does. Mounted
 * STANDALONE, though (the wizard's own unit tests do exactly that), nothing else owns the commit — and a
 * surface must never be left without a way to save, which is the same rule `EditorFooterSlot` follows for a
 * missing host. So the button exists exactly where it is the only option.
 */
export function SectionSave({ children }: { children: ReactNode }) {
  return useSaveRegistry() === null ? <>{children}</> : null;
}

/** Basics registers the create-or-return-existing step the fan-out runs first. */
export function useEnsureShow(fn: () => Promise<string>): void {
  const registry = useSaveRegistry();
  const latest = useRef(fn);
  latest.current = fn;

  useEffect(() => {
    if (!registry) return;
    registry.registerEnsureShow(() => latest.current());
    return () => registry.registerEnsureShow(null);
  }, [registry]);
}
